/**
 * api/axiosInstance.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The ONE shared axios client for SK Hospitality (mirrors SK Catalog's
 * src/api/axiosInstance.ts). Every service calls through here with an ABSOLUTE
 * URL from api/endpoints.ts.
 *
 * - withCredentials: true → sends the platform's httpOnly auth cookies on every
 *   request (auth is owned by skplatform-api; the cookie is host-only on
 *   localhost so it reaches both :3013 and the adapter on :3010).
 * - A Bearer header is added when a JS-held token exists (magic-link path).
 * - A response interceptor transparently refreshes an expired access token ONCE
 *   (POST /v1/auth/refresh) and replays the request. If the refresh fails, the
 *   original 401 is SURFACED (rejected) — it is NOT turned into a redirect here.
 *   That was the old bug: the interceptor called handoffToPlatform and returned
 *   a never-resolving promise, so callers' try/catch never ran and the app
 *   ping-ponged app ↔ platform forever. Re-auth is now decided by the boot gate
 *   (RequireAuth → checkSession), and background/adapter 401s fall through to
 *   the caller (e.g. bootstrapHospitality stays on the seeded demo state).
 * ──────────────────────────────────────────────────────────────────────────
 */
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { Platform_Refresh_Url } from './endpoints';
import { useAppStore } from '../store/useAppStore';

/** Authenticated client — cookie session + optional Bearer. Absolute URLs only. */
export const apiClient = axios.create({ withCredentials: true });

/** Unauthenticated client — /guest/* QR self-join (tenant/location from payload, no cookie). */
export const guestClient = axios.create({ withCredentials: false });

/* ── Request interceptor: attach the JS-held platform JWT when present ──
 * The adapter accepts either `Authorization: Bearer` or the httpOnly cookie.
 * The cookie rides along via withCredentials; this header covers the magic-link
 * case where a JWT is held in JS. 'session' is the cookie-only sentinel — never
 * send it as a bearer token. */
apiClient.interceptors.request.use((config) => {
  const token = useAppStore.getState().token;
  if (token && token !== 'session') {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

/* ── Silent refresh (single-flight) ──
 * A 401 from these endpoints is terminal (the refresh itself failing / logout);
 * everything else may refresh-and-retry once. */
const NO_REFRESH = ['/auth/refresh', '/auth/logout', '/auth/magic'];
let refreshInFlight: Promise<void> | null = null;

function refreshSession(): Promise<void> {
  if (!refreshInFlight) {
    refreshInFlight = apiClient
      .post(Platform_Refresh_Url)
      .then(() => undefined)
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

/* ── Response interceptor: refresh-once-then-reject (NO redirect) ── */
apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = error.response?.status;
    const url = original?.url ?? '';

    const refreshable =
      status === 401 && original && !original._retried && !NO_REFRESH.some((p) => url.includes(p));

    if (refreshable) {
      try {
        await refreshSession();
        original._retried = true;
        return apiClient(original);
      } catch {
        // Refresh failed — surface the original 401. The caller (checkSession /
        // bootstrapHospitality) decides what to do; the interceptor never
        // navigates, so there is no app ↔ platform redirect loop.
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);
